"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Compass, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityPhases = [{
  phase: "Peak Summer Surge",
  months: "April – June",
  icon: Sun,
  badge: "High Demand",
  summary: "Massive influx from Bengaluru, Chennai, and Coimbatore escaping plains heat.",
  strategy: "Dynamic minimum length of stay (MLOS), premium weekend rate fences, and zero panic discounting."
}, {
  phase: "Monsoon & Shoulder Window",
  months: "July – September",
  icon: CloudRain,
  badge: "Yield Stabilization",
  summary: "Intermittent rainfall and localized travel dips requiring proactive demand capture.",
  strategy: "Value-added leisure packages, workation/extended stay promotions, and corporate offsite targeting."
}, {
  phase: "Winter & Holiday Spike",
  months: "October – January",
  icon: Compass,
  badge: "High ADR",
  summary: "Pleasant chilly climate, festival breaks (Diwali/Christmas/New Year), and honeymooner demand.",
  strategy: "Early-bird pace tracking, non-refundable advance purchase rates, and premium room-type monetization."
}];
const challenges = [{
  id: 1,
  title: "Extreme Seasonality & Booking Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Ooty experiences acute weekend clustering and sharp holiday peaks contrasted by slow monsoon weeks. We implement structured minimum-stay rules, dynamic rate ladders, and booking pace triggers to maximize revenue before peak dates fill up.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based dynamic pricing & minimum stay (MLOS) fences"
}, {
  id: 2,
  title: "Regional Comp-Set Rate Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com across Ooty, Coonoor, and Kotagiri properties. Automated alerts prevent price wars while positioning your room rates at maximum yield.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA rate scraping & value-fenced tiering"
}, {
  id: 3,
  title: "OTA Over-Dependence & Commission Drain",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop losing 18–25% of gross revenues to online travel agents. We eliminate unauthorized B2B wholesaler leakage, restore meta-search rate parity, and convert high-intent Nilgiris travelers directly on your brand engine.",
  metricLabel: "Strategy focus",
  metricValue: "Rate parity enforcement & direct booking conversion funnels"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeooty" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Nilgiris Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Ooty
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ooty (Udhagamandalam) is driven by intense drive-to demand from Bengaluru, Chennai, and Coimbatore, marked by concentrated weekend spikes, high summer occupancy, and quiet monsoon shoulders. Sunil Singh structures disciplined, data-backed revenue strategies tailored for heritage estates, boutique resorts, and plantation stays across the Queen of Hill Stations.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-16 rounded-lg border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-6 border-b border-border/70">
            <div>
              <div className="flex items-center gap-2">
                <TrendingUp className="size-5 text-secondary" />
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                  Ooty Annual Seasonality & Demand Rhythm
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Calibrated pricing cycles to optimize RevPAR during peak Nilgiris demand while protecting baseline cash flow during shoulder periods.
              </p>
            </div>
            <Badge variant="secondary" className="self-start md:self-auto bg-secondary/15 text-secondary-foreground font-mono text-xs">
              Annual Cycle Analysis
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPhases.map((phase, idx) => {
            const IconComponent = phase.icon;
            return <div key={phase.phase} data-index={idx} className="flex flex-col justify-between rounded-lg border border-border/70 bg-background/50 p-5">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <IconComponent className="size-5 text-chart-2" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                        {phase.badge}
                      </span>
                    </div>
                    <h4 className="font-serif font-bold text-lg text-foreground">
                      {phase.phase}
                    </h4>
                    <p className="text-xs font-mono text-muted-foreground mt-0.5 mb-3 font-medium">
                      {phase.months}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {phase.summary}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/50 text-xs text-foreground">
                    <span className="font-semibold text-secondary">Playbook: </span>
                    <span className="text-muted-foreground">{phase.strategy}</span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Ooty hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}